import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Home, Bed, Bath, ChevronLeft, ChevronRight, DollarSign, Building2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BackButton from '@/components/BackButton';
import LeadCaptureModal from '@/components/LeadCaptureModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800';
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If it's a relative path, prepend the backend URL
  return `${BACKEND_URL}${imagePath}`;
};

const UnitDetail = () => {
  const { id } = useParams();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadModalTitle, setLeadModalTitle] = useState('');
  const [leadModalInterest, setLeadModalInterest] = useState('');
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchUnit();
  }, [id]);

  const fetchUnit = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/units/${id}`);
      const data = await response.json();
      setUnit(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching unit:', error);
      setLoading(false);
    }
  };

  const nextImage = () => {
    if (unit && unit.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % unit.images.length);
    }
  };

  const prevImage = () => {
    if (unit && unit.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + unit.images.length) % unit.images.length);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getStatusLabel = (status) => {
    if (status === 'Sold Out') return 'Sold Out';
    if (status === 'For Sale') return 'Condos Available';
    if (status === 'For Leasing') return 'Leasing Available';
    return status;
  };

  const getStatusColor = (status) => {
    if (status === 'Sold Out') return 'bg-gray-600';
    if (status === 'For Sale') return 'bg-green-600';
    if (status === 'For Leasing') return 'bg-blue-600';
    return 'bg-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#20B2AA] border-t-transparent"></div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unit not found</h2>
          <Link to="/units">
            <Button className="bg-[#20B2AA] hover:bg-[#189a93] text-white">
              Back to Units
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = unit.images && unit.images.length > 0 
    ? unit.images.map(img => getImageUrl(img))
    : ['https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <BackButton />

        {/* Image Gallery Grid */}
        <div className="mb-8">
          {images.length === 1 ? (
            /* Single Image */
            <div 
              className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl cursor-pointer group"
              onClick={() => {
                setLightboxIndex(0);
                setShowLightbox(true);
              }}
            >
              <img
                src={images[0]}
                alt={unit.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              
              {/* Status Badge */}
              <div className={`absolute top-6 left-6 ${getStatusColor(unit.status)} text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg z-10`}>
                {getStatusLabel(unit.status)}
              </div>

              {unit.featured && (
                <div className="absolute top-6 right-6 bg-yellow-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-10">
                  FEATURED
                </div>
              )}
            </div>
          ) : (
            /* Multiple Images Grid */
            <div className="grid grid-cols-2 gap-4">
              {/* Main Large Image */}
              <div 
                className="col-span-2 lg:col-span-1 relative h-[500px] rounded-2xl overflow-hidden shadow-xl cursor-pointer group"
                onClick={() => {
                  setLightboxIndex(0);
                  setShowLightbox(true);
                }}
              >
                <img
                  src={images[0]}
                  alt={`${unit.name} - Main`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                
                {/* Status Badge */}
                <div className={`absolute top-6 left-6 ${getStatusColor(unit.status)} text-white px-6 py-3 rounded-full text-sm font-semibold shadow-lg z-10`}>
                  {getStatusLabel(unit.status)}
                </div>

                {unit.featured && (
                  <div className="absolute top-6 right-6 bg-yellow-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-10">
                    FEATURED
                  </div>
                )}
              </div>

              {/* Smaller Images Grid */}
              <div className="col-span-2 lg:col-span-1 grid grid-cols-2 gap-4">
                {images.slice(1, 5).map((img, index) => (
                  <div 
                    key={index}
                    className="relative h-60 rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
                    onClick={() => {
                      setLightboxIndex(index + 1);
                      setShowLightbox(true);
                    }}
                  >
                    <img
                      src={img}
                      alt={`${unit.name} - ${index + 2}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />
                    {index === 3 && images.length > 5 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">+{images.length - 5} more</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lightbox Modal */}
        {showLightbox && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <button
              onClick={() => setShowLightbox(false)}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-50"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              onClick={() => setLightboxIndex((lightboxIndex - 1 + images.length) % images.length)}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-4 rounded-full backdrop-blur-sm transition-all z-50"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>

            <button
              onClick={() => setLightboxIndex((lightboxIndex + 1) % images.length)}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-4 rounded-full backdrop-blur-sm transition-all z-50"
            >
              <ChevronRight className="h-8 w-8" />
            </button>

            <div className="max-w-6xl w-full">
              <img
                src={images[lightboxIndex]}
                alt={`${unit.name} - Full size ${lightboxIndex + 1}`}
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              />
              <div className="text-white text-center mt-4 text-lg">
                {lightboxIndex + 1} / {images.length}
              </div>
            </div>
          </div>
        )}

        {/* Unit Details */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{unit.name}</h1>
              <div className="flex items-center text-gray-600 mb-4">
                <MapPin className="h-5 w-5 mr-2 text-[#20B2AA]" />
                <span className="text-lg">{unit.location}</span>
              </div>
              {unit.building_name && (
                <div className="flex items-center text-gray-600">
                  <Building2 className="h-5 w-5 mr-2 text-[#20B2AA]" />
                  <span className="text-lg">{unit.building_name}</span>
                </div>
              )}
            </div>

            {/* Key Features */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="bg-[#20B2AA]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <Bed className="h-8 w-8 text-[#20B2AA]" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{unit.bedrooms}</p>
                  <p className="text-sm text-gray-600">Bedrooms</p>
                </div>
                <div className="text-center">
                  <div className="bg-[#20B2AA]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <Bath className="h-8 w-8 text-[#20B2AA]" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{unit.bathrooms}</p>
                  <p className="text-sm text-gray-600">Bathrooms</p>
                </div>
                <div className="text-center">
                  <div className="bg-[#20B2AA]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <Home className="h-8 w-8 text-[#20B2AA]" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{unit.area}</p>
                  <p className="text-sm text-gray-600">Area</p>
                </div>
                <div className="text-center">
                  <div className="bg-[#20B2AA]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <Building2 className="h-8 w-8 text-[#20B2AA]" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{unit.property_type}</p>
                  <p className="text-sm text-gray-600">Type</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {unit.description && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                <div
                  className="rich-content"
                  dangerouslySetInnerHTML={{ __html: unit.description }}
                />
              </div>
            )}

            {/* Amenities */}
            {unit.amenities && unit.amenities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Amenities</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {unit.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="bg-green-100 rounded-full p-1">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <span className="text-gray-700">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location Map */}
            {unit.location_coords && unit.location_coords.lat && unit.location_coords.lng && (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Location</h2>
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${unit.location_coords.lat},${unit.location_coords.lng}&zoom=15`}
                  />
                </div>
                <div className="mt-4">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${unit.location_coords.lat},${unit.location_coords.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#20B2AA] hover:text-[#189a93] font-semibold"
                  >
                    <MapPin className="h-5 w-5" />
                    Get Directions
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Price and Contact */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-8 sticky top-24">
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">Price</p>
                <p className="text-4xl font-bold text-[#20B2AA] mb-2">{formatPrice(unit.price)}</p>
                <p className="text-sm text-gray-600">{unit.status}</p>
              </div>

              <div className="space-y-4">
                <Button 
                  onClick={() => {
                    setLeadModalTitle(`Schedule Viewing - ${unit.name}`);
                    setLeadModalInterest('Tenant');
                    setShowLeadModal(true);
                  }}
                  className="w-full bg-[#20B2AA] hover:bg-[#189a93] text-white py-6 text-lg rounded-xl"
                >
                  Schedule a Viewing
                </Button>
                <Button 
                  onClick={() => {
                    setLeadModalTitle(`Inquiry - ${unit.name}`);
                    setLeadModalInterest('Tenant');
                    setShowLeadModal(true);
                  }}
                  variant="outline" 
                  className="w-full border-2 border-[#20B2AA] text-[#20B2AA] hover:bg-[#20B2AA] hover:text-white py-6 text-lg rounded-xl"
                >
                  Contact Us
                </Button>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">Interested in this property?</p>
                <p className="text-gray-700">
                  Our team is ready to provide you with more information and arrange a personal tour.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        title={leadModalTitle}
        defaultInterest={leadModalInterest}
        source="rental"
        formLocation="unit_detail"
        propertyInterest={unit?.name}
      />
    </div>
  );
};

export default UnitDetail;
